/**
 * Shared icon set — every view pulls its SVG icons from here, so the app keeps a
 * single, consistent visual language instead of redefining glyphs per screen.
 *
 * Each icon is a function taking an optional Tailwind size class (an `h-/w-`
 * pair, e.g. `'h-6 w-6'`). Pass a **literal** string so Tailwind's source scan
 * (`@source` in styles.css) still picks the utility up — a computed class name
 * would be invisible to the scanner.
 */

/** Stroke-based icon on a 24×24 grid with round caps/joins. */
function stroke(paths, className = 'h-5 w-5', width = 1.6) {
  return `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="${width}" stroke-linecap="round" stroke-linejoin="round" class="${className}" aria-hidden="true">${paths}</svg>`;
}

export function logoSvg(size = '32') {
  return `<svg width="${size}" height="${size}" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M0 5C0 2.23858 2.23858 0 5 0H27C29.7614 0 32 2.23858 32 5V27C32 29.7614 29.7614 32 27 32H5C2.23858 32 0 29.7614 0 27V5Z" fill="#432DD8"/><path d="M9 12L15 18M8 18L14 12L16 9M6 9H18M11 6H12M26 26L21 16L16 26M18 22H24" stroke="white" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round"/></svg>`;
}

export const ICONS = {
  /* ---- Menu / navigation ------------------------------------------------- */
  phrases: (c) =>
    stroke(
      '<path d="M4 6.5A1.5 1.5 0 0 1 5.5 5h13A1.5 1.5 0 0 1 20 6.5v8a1.5 1.5 0 0 1-1.5 1.5H9l-4 3.5V16H5.5A1.5 1.5 0 0 1 4 14.5v-8Z"/>',
      c
    ),
  about: (c) =>
    stroke('<circle cx="12" cy="12" r="8.5"/><path d="M12 11v5"/><path d="M12 8h.01"/>', c),
  // Lightbulb — the "how to learn" guide.
  bulb: (c) =>
    stroke(
      '<path d="M9 18h6"/><path d="M10 21h4"/><path d="M12 3a6 6 0 0 0-3.5 10.9c.6.45.95 1.13.95 1.85V16h5.1v-.25c0-.72.35-1.4.95-1.85A6 6 0 0 0 12 3Z"/>',
      c
    ),
  trash: (c) =>
    stroke(
      '<path d="M4 7h16"/><path d="M9 7V5.5A1.5 1.5 0 0 1 10.5 4h3A1.5 1.5 0 0 1 15 5.5V7"/><path d="M6 7l1 12.5A1.5 1.5 0 0 0 8.5 21h7a1.5 1.5 0 0 0 1.5-1.5L18 7"/>',
      c
    ),
  logout: (c) =>
    stroke(
      '<path d="M14 8V6a2 2 0 0 0-2-2H6a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h6a2 2 0 0 0 2-2v-2"/><path d="M18 15l3-3-3-3"/><path d="M21 12H9"/>',
      c
    ),
  hamburger: (c) => stroke('<path d="M4 7h16M4 12h16M4 17h16"/>', c, 1.8),
  close: (c) => stroke('<path d="m6 6 12 12M18 6 6 18"/>', c, 1.8),

  /* ---- Directional chevrons ---------------------------------------------- */
  chevronRight: (c) => stroke('<path d="m9 6 6 6-6 6"/>', c, 1.8),
  chevronLeft: (c) => stroke('<path d="m15 6-6 6 6 6"/>', c, 1.8),

  /* ---- Status / content -------------------------------------------------- */
  check: (c = 'h-4 w-4') => stroke('<path d="m5 13 4 4L19 7"/>', c, 2.2),
  image: (c) =>
    stroke(
      '<rect x="3" y="4" width="18" height="16" rx="2"/><circle cx="8.5" cy="9.5" r="1.5"/><path d="m21 15-5-5L7 19"/>',
      c
    ),
  download: (c = 'h-7 w-7') =>
    stroke(
      '<path d="M12 3v12m0 0 4-4m-4 4-4-4"/><path d="M4 17v2a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-2"/>',
      c
    ),

  /* ---- Login highlights --------------------------------------------------- */
  headphones: (c) =>
    stroke(
      '<path d="M3 14h3a2 2 0 0 1 2 2v3a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-7a9 9 0 0 1 18 0v7a2 2 0 0 1-2 2h-1a2 2 0 0 1-2-2v-3a2 2 0 0 1 2-2h3"/>',
      c
    ),
  book: (c) =>
    stroke(
      '<path d="M12 7v14"/><path d="M3 18a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1h5a4 4 0 0 1 4 4 4 4 0 0 1 4-4h5a1 1 0 0 1 1 1v13a1 1 0 0 1-1 1h-6a3 3 0 0 0-3 3 3 3 0 0 0-3-3H3Z"/>',
      c
    ),
  repeat: (c) =>
    stroke(
      '<path d="m17 2 4 4-4 4"/><path d="M3 11v-1a4 4 0 0 1 4-4h14"/><path d="m7 22-4-4 4-4"/><path d="M21 13v1a4 4 0 0 1-4 4H3"/>',
      c
    ),

  /* ---- Fill-based glyphs -------------------------------------------------- */
  // Media transport — used by the shared audio Player and per-sentence controls.
  play: (c = 'h-6 w-6') =>
    `<svg viewBox="0 0 24 24" fill="currentColor" class="${c}" aria-hidden="true"><path d="M8 5.14v13.72a1 1 0 0 0 1.54.84l10.29-6.86a1 1 0 0 0 0-1.68L9.54 4.3A1 1 0 0 0 8 5.14Z"/></svg>`,
  pause: (c = 'h-6 w-6') =>
    `<svg viewBox="0 0 24 24" fill="currentColor" class="${c}" aria-hidden="true"><rect x="6" y="5" width="4" height="14" rx="1"/><rect x="14" y="5" width="4" height="14" rx="1"/></svg>`,

  /* ---- Favorites --------------------------------------------------------- */
  // Heart toggle: outline for "not favorited", filled for "favorited".
  heart: (c = 'h-6 w-6') =>
    stroke(
      '<path d="M21 8.25c0-2.485-2.099-4.5-4.688-4.5-1.935 0-3.597 1.126-4.312 2.733-.715-1.607-2.377-2.733-4.313-2.733C5.1 3.75 3 5.765 3 8.25c0 7.22 9 12 9 12s9-4.78 9-12Z"/>',
      c,
      1.8
    ),
  heartFilled: (c = 'h-6 w-6') =>
    `<svg viewBox="0 0 24 24" fill="currentColor" class="${c}" aria-hidden="true"><path d="M21 8.25c0-2.485-2.099-4.5-4.688-4.5-1.935 0-3.597 1.126-4.312 2.733-.715-1.607-2.377-2.733-4.313-2.733C5.1 3.75 3 5.765 3 8.25c0 7.22 9 12 9 12s9-4.78 9-12Z"/></svg>`,

  /* ---- Dictionary -------------------------------------------------------- */
  // Star toggle: outline for "not saved", filled for "saved". Same five-point
  // path in both, so the glyph doesn't shift when it flips.
  star: (c = 'h-6 w-6') =>
    stroke(
      '<path d="M12 3.75l2.6 5.27 5.82.85-4.21 4.1.99 5.78L12 17.02l-5.2 2.73.99-5.78-4.21-4.1 5.82-.85L12 3.75Z"/>',
      c,
      1.8
    ),
  starFilled: (c = 'h-6 w-6') =>
    `<svg viewBox="0 0 24 24" fill="currentColor" class="${c}" aria-hidden="true"><path d="M12 3.75l2.6 5.27 5.82.85-4.21 4.1.99 5.78L12 17.02l-5.2 2.73.99-5.78-4.21-4.1 5.82-.85L12 3.75Z"/></svg>`,
};
